import { useEffect, useState } from "react";

/**
 * How much of the screen the system keyboard is covering, and what is left.
 *
 * Two surfaces need this and they need the same answer, which is why it is not
 * in either of them: the add sheet, which is fixed to the visual viewport and
 * would otherwise sit behind the keyboard, and the pot flow, which is a bounded
 * column with a docked field at the bottom of it and would otherwise dock that
 * field behind the keyboard. The rule is the same in both places — *the
 * keyboard takes space away from the surface, it does not push it* — and it has
 * to be measured because iOS Safari does not shrink the layout viewport when
 * the keyboard opens.
 */

/**
 * `visualViewport` rather than `window.innerHeight` because on iOS those two
 * disagree by exactly the height of the keyboard, and `innerHeight` is the one
 * that lies. `offsetTop` is included because the page can be scrolled *within*
 * the visual viewport when a focused field is pushed up, and the inset is the
 * gap at the bottom rather than the difference in heights.
 *
 * Falls back to the layout viewport where there is no `visualViewport` at all,
 * which is a desktop browser, where the answer is "no keyboard" and correct.
 */
export function useKeyboard(): { available: number; inset: number } {
  const read = () => {
    const vv = window.visualViewport;
    if (!vv) return { available: window.innerHeight, inset: 0 };
    return {
      available: vv.height,
      inset: Math.max(0, window.innerHeight - (vv.height + vv.offsetTop)),
    };
  };

  const [state, setState] = useState(read);

  useEffect(() => {
    const vv = window.visualViewport;
    const update = () => setState(read());
    update();
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return state;
}
